// ============================================================================
// ui/pdf-sheets.js — the rep's PDF documents: the registry, and the dialog
// ============================================================================
// The Gym Layout Designer gives a rep three documents off one button (Layout Only,
// Full Packet, Seam Plan). The Weight Designer is heading the same way — Ryan
// 2026-07-29: *"we're likely going to have a few different PDF versions this thing can
// produce."* So the button is built as a LIST from the start, even though the list has
// one entry today. A second document is then a record in PDF_TYPES plus a branch in the
// worker's generator — not a rewrite of the button, the card, or the dialog.
//
// WHAT TODAY'S DOCUMENT IS. The sheet that exists is an approval / sign-off sheet: the
// plate, the spec, the colours with hexes a printer can match, and the view-only link.
// Ryan: *"it'll require a long session to perfect, but for now you can save it as such in
// terms of the name."* So it is NAMED for what it is rather than left as the generic
// "design sheet", which would quietly become wrong the moment a second type lands.
//
// REP-ONLY. `pdfItems()` returns [] off the portal, and an empty list means
// canvas-actions never builds the third button at all — a buyer sees Save · Share exactly
// as before.
// ============================================================================

import { h } from './dom.js';
import { openModal } from './modal.js';
import { downloadDesignSheet } from './share.js';
import { isRepMode } from '../rep-mode.js';
import { isExternalPortalUser } from '../portal-api.js';
/* The same "is this an admin" answer the showcase publisher asks — one predicate, so a session
   that can publish to the storefront parade and a session that can build the supplier's sheet can
   never disagree about what an admin is. */
import { isShowcaseAdmin } from './showcase-admin.js';

/**
 * The GLD's PDF icon, verbatim (`#rdPortalPdfBtn` in sections/rep-quote-gym-designer.liquid):
 * a dog-eared page with **"PDF" lettered on it**. Ryan 2026-07-29: *"the PDF icon, like in
 * the GLD, needs to look more like a PDF with 'PDF' on it."* A bare document outline is what
 * the first cut used, and next to a floppy disk and a share node it reads as "some document"
 * rather than "a PDF" — the lettering is the whole affordance.
 *
 * ⚠ The `<text>` needs `stroke="none"`: the wrapper sets `stroke="currentColor"`, and a
 * stroked 7px glyph fills in and turns to mush at 18px.
 */
export const ICON_PDF =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>' +
  '<text x="12" y="17" text-anchor="middle" fill="currentColor" stroke="none" font-size="7" font-weight="700" font-family="sans-serif">PDF</text></svg>';

/**
 * Every document a rep can generate.
 *
 * `id` is the wire value — it goes to the worker as `variant`, which whitelists it, so
 * adding a type here without adding it there fails loudly rather than silently producing
 * the wrong document.
 */
export const PDF_TYPES = [
  {
    id: 'approval',
    /* ⚠ THE ID IS THE WIRE VALUE AND STAYS `approval` THROUGH EVERY RENAME. The worker whitelists
       it, older R2 keys carry its slug, and nothing a reader sees comes from it — the label, the
       band and the filename are all separate strings. Renaming it would break generation to buy
       nothing. (Ryan 2026-08-27: *"Name it 'Customer Design Sheet'"*.) */
    label: 'Customer Design Sheet',
    sub: 'The specs we send customers',
    // What the dialog says above the fields. Per-type, because a future quote-facing or
    // production-facing document is asking for the same two facts for different reasons.
    blurb: 'Cover page plus a full spec sheet — the plate, its colours with printable hex values, and the view-only link.',
  },
  /* THE SUPPLIER'S COPY (Ryan 2026-08-27) — *"another one like it, without the fancy cover page,
     which is what I submit to our supplier that shows them everything they're designing… only
     admins can generate."*

     ⚠ `adminOnly` HIDES THE ROW; IT DOES NOT PROTECT THE DOCUMENT. `/wd-pdf` re-checks `rep.admin`
     for any variant carrying this flag, because a menu row is a convenience and a fetch is not
     bound by one. Both halves, the same rule as the showcase publisher.
       ⚠ It also EXISTS to be listed here rather than branched into `openPdfDialog`: the whole
     reason this file was built as a registry on day one was that a second document should be a
     record, not a rewrite. This is that second record — the button, the card and the dialog did
     not change to accommodate it. */
  {
    id: 'vendor',
    label: 'Vendor Design Sheet',
    sub: 'The build spec we send the factory',
    blurb: 'No cover page. Every product designed, the colours with hex values and what each is on, where to get the font, the print-ready logo, and where to ship a sample swatch.',
    adminOnly: true,
    /* Declares that this document asks for a contact name (Ryan 2026-09-02) — the factory files a
       repeat order by it. Only the vendor sheet prints one, so only the vendor sheet asks. */
    asksContact: true,
    // Every product picked, rendered and tiled onto one image — a factory quoting a set needs to
    // see each item, not whichever one the designer happened to be standing on.
    allProducts: true,
  },
];

/* ── WHO THIS IS FOR, REMEMBERED ──────────────────────────────────────────────────────────────
   `sessionStorage`, not local: the answer belongs to the rep's current sitting with one customer,
   and a name still sitting in the box tomorrow morning is a worse failure than an empty box — it is
   the wrong customer's name on a document, pre-filled and easy to miss. Scoped to the tab for the
   same reason a rep may have two designs open in two tabs.
   ⚠ Wrapped: storage throws outright in some privacy modes, and a remembered convenience must never
   be able to stop a document being generated. */
const CUSTOMER_KEY = 'wd:v2:pdf-customer';

function rememberedCustomer() {
  try { return (sessionStorage.getItem(CUSTOMER_KEY) || '').trim(); } catch { return ''; }
}

function rememberCustomer(name) {
  try {
    if (name) sessionStorage.setItem(CUSTOMER_KEY, name);
    else sessionStorage.removeItem(CUSTOMER_KEY);
  } catch { /* nothing here is worth failing a document over */ }
}

/** Where the vendor sheet's contact name is remembered between two clicks, like the org name. */
const CONTACT_KEY = 'wd:pdf:contact';

function rememberedContact() {
  try { return sessionStorage.getItem(CONTACT_KEY) || ''; } catch { return ''; }
}

function rememberContact(name) {
  try {
    if (name) sessionStorage.setItem(CONTACT_KEY, name);
    else sessionStorage.removeItem(CONTACT_KEY);
  } catch { /* nothing here is worth failing a document over */ }
}

/** A labelled text input that reads back off the returned node. */
function field(id, label, placeholder, { optional = false, short = false, value = '' } = {}) {
  const input = h('input', {
    type: 'text', id, class: `wd-pdf-field__input${short ? ' wd-pdf-field__input--short' : ''}`,
    placeholder, maxlength: short ? '10' : '60', autocomplete: 'off', value,
  });
  const wrap = h('label', { class: 'wd-pdf-field', for: id },
    h('span', { class: 'wd-pdf-field__label' }, label,
      optional ? h('span', { class: 'wd-pdf-field__opt' }, ' (optional)') : null),
    input);
  wrap.input = input;
  return wrap;
}

/**
 * Ask for the one fact the document needs, then build it.
 *
 * WHO IT IS FOR, and nothing else. That name is what the cover leads with — the WD now passes it
 * to the shared renderer as the `projectName`, which is the line printed large, so the cover reads
 * "PROPOSAL FOR: ALLISON RIVAS" rather than naming the tool. The rep's own name, email and phone
 * come from the portal session, never from this dialog, because a rep typing their own contact
 * details on every document is a transcription error waiting to happen.
 *
 * ⚠ It was TWO fields until 2026-08-27; see the ZIP note below for why the second one went.
 */
export async function openPdfDialog(ctx, type) {
  /* Seeded in the order the answer is most likely to be right:
       1. what was typed the LAST time a sheet was generated (Ryan 2026-08-27: *"any time a name is
          added… save it so you don't need to retype it each time a document is generated"* — a rep
          building the customer's copy and then the factory's copy is the ordinary case, and
          retyping a school name between two clicks is a transcription error waiting to happen),
       2. the plate's own org name.
     Never blank when either exists, and always overwritable. */
  const org = (ctx.state?.designProfile?.orgName || '').trim();
  const customer = field('wd-pdf-customer', 'Customer / school name', 'e.g., Hot Springs High School',
    { value: rememberedCustomer() || org });
  const status = h('p', { class: 'wd-pdf-status', role: 'status' });

  /* THE CONTACT, VENDOR SHEET ONLY (Ryan 2026-09-02): *"They need a customer name so they can save
     the file in case they order again… I want that to simply be [F. Last]."*

     ⚠ A SECOND FIELD RATHER THAN A REFORMAT OF THE FIRST, because the first one holds a SCHOOL
     ("Arcadia High School"), and initialising that gives "A. School". They are different facts and
     one box cannot hold both.
     ⚠ OPTIONAL, AND BLANK MEANS THE ROW IS NOT DRAWN — the sheet named nobody until today and has
     to stay valid naming nobody. `asksContact` lives on the registry record so a future document
     opts in by declaring it, which is the same reason the list exists at all. */
  const contact = type.asksContact
    ? field('wd-pdf-contact', 'Customer contact', 'e.g., John Smith',
      { optional: true, value: rememberedContact() })
    : null;

  /* ⛔ THE ZIP FIELD IS GONE (Ryan 2026-08-27: *"No need to ask the rep/admin for a zip code… And no
     need to print the zip or city or state on the PDF. That's too much."*). It was there because the
     shared cover renderer prints a `location` line; the WD cover no longer asks for one, so the
     question had nothing left to feed. A field whose answer is not printed is pure friction. */
  const body = h('div', { class: 'wd-pdf-form' },
    h('p', { class: 'wd-pdf-blurb' }, type.blurb),
    customer, contact, status);

  const res = await openModal({
    mount: ctx.el,
    title: type.label,
    body,
    actions: [
      { id: 'go', label: 'Generate PDF', variant: 'primary' },
      { id: 'cancel', label: 'Cancel', variant: 'ghost' },
    ],
  });
  if (res !== 'go') return false;

  // The modal is already gone by here, so progress and failure are narrated on the status
  // bar — the same surface every other slow action in the tool reports to.
  ctx.status?.info?.('Building the ' + type.label.toLowerCase() + '…');
  const customerName = customer.input.value.trim();
  rememberCustomer(customerName);
  /* ⛔ VERBATIM (Ryan 2026-09-02: *"use the name I type in… So 'A. Rivas' can be 'Allison Rivas'
     — that's fine"*). This field was initialised to "F. Last" for one afternoon, on the reasoning
     that a factory needs a filing handle rather than an identity. Ryan has since decided the full
     name is fine, so the abbreviation — and the surname-particle handling that went with it — is
     gone rather than left dormant: an unused formatter with an elaborate rationale beside it is how
     the next person concludes the rule still applies. `clean` still sanitises it at the PDF, which
     is a font-encoding requirement and not a policy. */
  const typedContact = contact ? contact.input.value.trim() : '';
  rememberContact(typedContact);
  return downloadDesignSheet(ctx, {
    customerName,
    contactName: typedContact,
    variant: type.id,
    // The two things about a document that change how it is BUILT, passed rather than re-derived:
    // share.js has no view of this registry (it is the one this file imports FROM, so reading it
    // back would be a cycle).
    allProducts: !!type.allProducts,
    admin: !!type.adminOnly,
  });
}

/** Card rows for the PDF cluster button. Empty off the portal → no button is built. */
export function pdfItems(ctx) {
  if (!isRepMode()) return [];
  /* CUSTOM_PORTALACCESS_BY_CLAUDE (Ryan 2026-10-30) — an external user makes pictures, not design
     sheets: "we can remove the ability for him to generate PDF's". No PDF button is built. */
  if (isExternalPortalUser()) return [];
  const admin = isShowcaseAdmin();
  return PDF_TYPES.filter((t) => !t.adminOnly || admin).map((t) => ({
    icon: ICON_PDF,
    label: t.label,
    sub: t.sub,
    onClick: () => { void openPdfDialog(ctx, t); },
  }));
}
